import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RouteHighlight, RouteStop } from '../maps/JourneyRouteMap';
import type { TimelineHoverItem } from './components/RouteTimeline';
import { SPLIT_MIN_WIDTH } from './components/DetailSplitLayout';
import useViewportWidth from '../../hooks/useViewportWidth';
import { CAN_HOVER } from '../../shared/canHover';
import { stopSequenceLabels } from './utils';

// Tablets pin the map in the page's sticky header band (`tabletHeaderMap`).
const TABLET_MIN_WIDTH = 768;

export type JourneyMapPlacement = 'beside' | 'header' | 'inline';

interface JourneyForMap {
  stops?: any[];
  legs?: any[];
}

interface Options {
  journey: JourneyForMap | null;
  /** An open leg or stop sheet keeps the map on its item. */
  openLegId: string | null;
  openStopId: string | null;
  /** A click on the stop picked on the map opens its sheet. */
  onOpenStop: (stopId: string) => void;
  /** The page pins the map in a sticky header band on tablets (the owner's
   *  Journey detail); without one, the map stays inline below the split. */
  tabletHeaderMap?: boolean;
}

/**
 * The route map's behaviour on a journey page, one implementation for the
 * owner's Journey detail and the shared journey view
 * (/s/<token>/journeys/<id>). Convention: docs/codebase/ui-conventions.md
 * ("Desktop split: map on the right").
 *
 * - `placement`: 'beside' on desktop (DetailSplitLayout's right panel),
 *   'header' on tablets where the page has a sticky header band, else
 *   'inline' (the map scrolls away with the page).
 * - `routeStops`: the stops the map draws, numbered like the timeline
 *   (stopSequenceLabels, counted over all stops before dropping the ones
 *   without coordinates).
 * - `highlight`: the stop or leg hovered in the timeline, or the one whose
 *   sheet is open (it wins: the sheet covers the timeline). For the maps that
 *   stay in view, 'beside' and 'header'.
 * - Map pick (desktop): a click on a stop picks it (its timeline card is
 *   outlined and scrolled to, its pin stays), a click on the picked pin
 *   opens its sheet, a click on the map away from the stops clears it.
 *
 * Spread `timelineProps` into RouteTimeline and `sideMapProps` into the
 * 'beside' JourneyRouteMap.
 */
export function useJourneyRouteMap({ journey, openLegId, openStopId, onOpenStop, tabletHeaderMap = false }: Options) {
  const viewportWidth = useViewportWidth();
  const placement: JourneyMapPlacement =
    viewportWidth >= SPLIT_MIN_WIDTH ? 'beside'
      : tabletHeaderMap && viewportWidth >= TABLET_MIN_WIDTH ? 'header'
        : 'inline';

  // Clearing a hover waits a beat, so running the cursor down the timeline
  // doesn't flash the whole route between two rows.
  const [hoverItem, setHoverItem] = useState<TimelineHoverItem | null>(null);
  const hoverClearRef = useRef<number | undefined>(undefined);
  const handleHoverItem = useCallback((item: TimelineHoverItem | null) => {
    window.clearTimeout(hoverClearRef.current);
    if (item) setHoverItem(item);
    else hoverClearRef.current = window.setTimeout(() => setHoverItem(null), 120);
  }, []);
  useEffect(() => () => window.clearTimeout(hoverClearRef.current), []);

  const focusItem = useMemo<TimelineHoverItem | null>(
    () => (openLegId ? { kind: 'leg', id: openLegId } : openStopId ? { kind: 'stop', id: openStopId } : hoverItem),
    [openLegId, openStopId, hoverItem],
  );
  const highlight = useMemo<RouteHighlight | null>(() => {
    if (!focusItem) return null;
    if (focusItem.kind === 'stop') return { kind: 'stop', stopId: focusItem.id };
    const leg = (journey?.legs || []).find((l: any) => l.id === focusItem.id);
    return leg ? { kind: 'leg', fromStopId: leg.fromStopId, toStopId: leg.toStopId } : null;
  }, [focusItem, journey]);

  const [pickedStopId, setPickedStopId] = useState<string | null>(null);
  const handleStopClick = useCallback((stopId: string | number) => {
    const id = String(stopId);
    if (id === pickedStopId) onOpenStop(id);
    else setPickedStopId(id);
  }, [pickedStopId, onOpenStop]);
  const clearPick = useCallback(() => setPickedStopId(null), []);

  const routeStops = useMemo<RouteStop[]>(() => {
    const stops = journey?.stops ?? [];
    const legs: any[] = journey?.legs ?? [];
    const ordered = [...stops].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
    const seq = stopSequenceLabels(ordered);
    return ordered
      .map((s: any, i: number) => {
        const label = seq[i];
        return {
          id: s.id,
          lat: parseFloat(s.latitude),
          lng: parseFloat(s.longitude),
          label: s.city || s.location || '',
          arrivalMode: legs.find((l) => l.toStopId === s.id)?.transportMode ?? null,
          number: label.kind === 'stop' ? label.number : null,
        };
      })
      .filter((s) => isFinite(s.lat) && isFinite(s.lng));
  }, [journey]);

  return {
    placement,
    routeStops,
    highlight,
    timelineProps: {
      // Only where the map stays in view while the timeline scrolls, and the
      // device really hovers.
      onHoverItem: CAN_HOVER && placement !== 'inline' ? handleHoverItem : undefined,
      selectedStopId: placement === 'beside' ? pickedStopId : null,
    },
    sideMapProps: {
      onStopClick: handleStopClick,
      onBackgroundClick: clearPick,
      selectedStopId: pickedStopId,
    },
  };
}
